import { pick } from 'lodash'
import QueryString from 'qs'
import * as Yup from 'yup'
import { apiClient } from '../../api/client'
import { FormValue, SelectOption } from '../../api/globalTypes'
import i18n from '../../i18n'
import { Field, FieldGroup } from './types'

export function addRequiredValidation(
    validationSchema: Record<string, Yup.AnySchema>,
    fields: (Field | FieldGroup)[],
    message = i18n.t('validation.message')
) {
    for (const field of fields) {
        if ('required' in field && field.required && validationSchema[field.name]) {
            validationSchema[field.name] = validationSchema[field.name].required(message)
        }

        if ('children' in field && field.children) {
            const filteredChildren = field.children.filter(Boolean) as (Field | FieldGroup)[]
            addRequiredValidation(validationSchema, filteredChildren, message)
        }
    }
}

/**
 * Fetches static form select options (eg. statuses, types, etc.) for different modules
 */
export const fetchFormValues = async <T>(
    endpointsConfig: Record<string, string>,
    formFilters?: Record<string, unknown>,
    endpointFilters?: Record<string, string[]>
): Promise<T> => {
    const entries = Object.entries(endpointsConfig)

    const fetchPromises = entries.map(async ([key, endpoint]) => {
        const acceptedFilters = endpointFilters?.[key] || []
        const selectedFilters = formFilters && acceptedFilters.length > 0 ? pick(formFilters, acceptedFilters) : undefined

        const response = await apiClient(endpoint, {
            params: selectedFilters && Object.keys(selectedFilters).length > 0 ? { filter: selectedFilters } : undefined,
            paramsSerializer: (params: Record<string, unknown>) => {
                return QueryString.stringify(params)
            },
        })

        return { key, data: response.data }
    })

    const results = await Promise.allSettled(fetchPromises)

    return results.reduce((acc, curr, index) => {
        const [key] = entries[index]

        if (curr.status === 'fulfilled') {
            acc[curr.value.key] = curr.value.data
        } else {
            console.error(`Failed to fetch ${key}:`, curr.reason)
            acc[key] = []
        }

        return acc
    }, {} as Record<string, FormValue[]>) as T
}

/**
 * Transforms form value data from API format to react-select format
 */
export const formatToSelectOptions = (data?: FormValue[]): SelectOption[] => {
    if (!data || !Array.isArray(data)) return []

    return data.map((item) => ({
        value: item.id.toString(),
        label: item.name,
    }))
}

/**
 * Maps form values object from API format to react-select format
 * @param data - Raw data from API with FormValue[] values
 * @returns Object with same keys but SelectOption[] values
 */
export const mapValuesToSelectOptions = (data: Record<string, FormValue[] | undefined> | undefined): Record<string, SelectOption[]> => {
    if (!data) return {}

    return Object.entries(data).reduce((acc, [key, value]) => {
        if (value) {
            acc[key] = formatToSelectOptions(value)
        }
        return acc
    }, {} as Record<string, SelectOption[]>)
}

/**
 * Maps already-formatted form values data to field names based on provided config
 * @param data - Already formatted data with SelectOption[] values
 * @param fieldMapping - Config mapping data keys to field names (e.g., { projectStatus: 'project_status_id' })
 * @returns Object with field names as keys and SelectOption[] as values
 */
export const mapValuesToFormFields = (
    data: Record<string, SelectOption[]> | undefined,
    fieldMapping: Record<string, string>
): Record<string, SelectOption[]> => {
    if (!data) return {}

    return Object.entries(data).reduce((acc, [key, value]) => {
        const fieldKey = fieldMapping[key]

        if (fieldKey && value) {
            acc[fieldKey] = value
        }

        return acc
    }, {} as Record<string, SelectOption[]>)
}

/**
 * Helper to conditionally enable FormValues queries based on current module
 * @param modules - List of modules that should enable the query
 * @param currentModule - Current active module
 * @param filterValue - Optional filter value that must be truthy for the query to be enabled
 * @returns Object with enabled flag for useQuery options
 */
export const enableFormValues = (modules: string[], currentModule: string, filterValue?: string | number | string[] | undefined) => {
    const isModuleEnabled = modules.includes(currentModule)

    if (filterValue !== undefined) {
        const hasValidFilter = Array.isArray(filterValue) ? filterValue.length > 0 : Boolean(filterValue)
        return { enabled: isModuleEnabled && hasValidFilter }
    }

    return { enabled: isModuleEnabled }
}
